import { type FC, type ReactNode, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import ContentSpinner from '@/views/components/Spinners/ContentSpinner';

export interface InfiniteScrollProps {
  children: ReactNode;
  hasMore?: boolean;
  isLoading?: boolean;
  loadMore: () => void;
  className?: string;
  /**
   * How early before the bottom to trigger the next page.
   */
  scrollOffset?: number;
}

export const InfiniteScroll: FC<InfiniteScrollProps> = ({
  children,
  hasMore = false,
  isLoading = false,
  loadMore,
  className,
  scrollOffset = 800,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(false);

  isLoadingRef.current = isLoading;
  hasMoreRef.current = hasMore;

  const { ref: bottomRef } = useInView({
    onChange: (inView) => {
      if (inView && hasMoreRef.current && !isLoadingRef.current) {
        isLoadingRef.current = true;
        loadMore();
      }
    },

    root: containerRef.current,
    rootMargin: `0px 0px ${scrollOffset}px 0px`,
    threshold: 0,
  });

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      {children}

      {hasMore && <div ref={bottomRef} aria-hidden="true" />}

      {isLoading && (
        <div className="d-flex justify-content-center py-16">
          <ContentSpinner size="large" height="auto" />
        </div>
      )}
    </div>
  );
};
